import { useCallback, useMemo } from 'react'
import { useSharedState } from '../../shared/helpers/useSharedState'
import type { UploadFile, UploadFileNative, UploadProps } from './types'

export type UseUploadReturn = {
  files: Array<UploadFile>
  setFiles: (files: Array<UploadFile | UploadFileNative>) => void
  clearFiles: () => void
  internalFiles: Array<UploadFile>
  setInternalFiles: (files: Array<UploadFile>) => void
  getExistingFile: (
    file: File,
    fileItems?: Array<UploadFile>
  ) => UploadFile
}

/**
 * Use together with Upload with the same id to manage the files from outside the component.
 */
function useUpload(id: UploadProps['id']): UseUploadReturn {
  const { data, extend } = useSharedState<{
    files?: Array<UploadFile>
    internalFiles?: Array<UploadFile>
  }>(id)

  const clearFiles = useCallback(() => {
    extend({
      files: [],
      internalFiles: [],
    })
  }, [extend])

  const setFiles = useCallback(
    (files: Array<UploadFile | UploadFileNative>) => {
      const newFiles = files?.filter(
        (file) => file?.file instanceof File
      ) as UploadFile[]
      extend({
        files: newFiles,
      })
    },
    [extend]
  )

  const setInternalFiles = useCallback(
    (internalFiles: Array<UploadFile>) => {
      extend({ internalFiles })
    },
    [extend]
  )

  const files = useMemo(() => data?.files || [], [data?.files])
  const internalFiles = useMemo(
    () => data?.internalFiles || [],
    [data?.internalFiles]
  )

  const getExistingFile = useCallback(
    (file: File, fileItems: Array<UploadFile> = files) => {
      return fileItems.find(({ file: f }) => {
        return isFileEqual(file, f)
      })
    },
    [files]
  )

  return {
    files,
    setFiles,
    clearFiles,
    internalFiles,
    setInternalFiles,
    getExistingFile,
  }
}

export const isFileEqual = (fileA: File, fileB: File): boolean => {
  const compareExistingProperty = function (
    a: File,
    b: File,
    property: string
  ) {
    const key = property as keyof File
    return (
      a &&
      property in a &&
      b &&
      property in b &&
      (a[key] === 0 || // If value is 0, which is default when not provided, we can't say whether the file is equal or not, so we assume they are.
        b[key] === 0 || // If value is 0, which is default when not provided, we can't say whether the file is equal or not, so we assume they are.
        a[key] === b[key])
    )
  }
  return (
    fileA.name === fileB.name &&
    compareExistingProperty(fileA, fileB, 'size') &&
    compareExistingProperty(fileA, fileB, 'lastModified')
  )
}

export default useUpload
